import React, { useRef, useState, useEffect, memo } from 'react';
import { useSelector } from 'react-redux';
import { Spinner, Icon } from '@iq/react-components';
import classnames from 'classnames';

import { getActiveSite } from '../../../../../bundles/sites';
import { useSubscription, useRefetchOnRangeChange } from '../../../../../bundles/visualizations';
import { getTimezone } from '../../../../../bundles/application';
import { getSiteVariablesIndex } from '../../../../../bundles/sources';
import { useClientSize } from '../../../../../utils';
import { DropdownMenu } from '../../../../DropdownMenu';

import Loader from '../../../../Loader';
import {
  getCurrentPentagonArea,
  getGasCentroid,
  getPentagonAreas,
  normalizeCoordinate,
  tooltipLabelFormatter,
} from './helpers';
import {
  PentagonSummitLabels,
  PentagonLegend,
  PentagonOutline,
  PentagonAreaLines,
  PentagonAreaHighlights,
  PointToolTip,
  RollingAveragePlot,
  DrawPentagonArea,
} from './components';
import { useDuvalPentagonData } from './hooks/useDuvalPentagonData';
import { useDuvalPentagonSeries } from './hooks/useDuvalPentagonSeries';
import { usePentagonRolling } from './hooks/usePentagonRolling';
import { resolveColor } from '../../../../../colors';

/**
 * Duval Pentagon: diagnoses transformer faults from dissolved gas analysis (DGA).
 * Plots the relative proportions of 5 dissolved gases (H2, C2H6, CH4, C2H4, C2H2)
 * as a centroid inside a pentagon, and the colored zone the centroid lands in
 * indicates the likely fault type.
 *
 * Supports Pentagon types 1 and 2, which use the same gases but different zones:
 *   - Type 1: PD, S, T1, T2, T3, D1, D2
 *   - Type 2: PD, S, O, C, T3-H, D1, D2
 */

function AreaToolTip({ position, text }) {
  return (
    <div
      className="tooltip"
      style={{ left: position.x, top: position.y }}
    >
      {text}
    </div>
  );
}

const DuvalPentagonVisualization = memo(
  ({
    visualization,
    panelId,
    pollingDateRange,
    onExportData,
    onFullscreen,
    isPreview,
    isFullscreen,
    selected,
    onSelect,
    openSignalViewer,
    CustomTooltip,
    getMoreOptions,
    dropdownPlacement,
    signalCounts,
    visualizationName,
    hideToolbar,
  }) => {
    const site = useSelector(getActiveSite);
    const timezone = useSelector(getTimezone);
    const variablesIndex = useSelector(getSiteVariablesIndex);

    const [legendHeight, setLegendHeight] = useState(0);

    const [errors, setErrors] = useState([]);

    const [isAreaHighlighted, setIsAreaHighlighted] = useState({});

    const [areaTooltip, setAreaTooltip] = useState({
      show: false,
      position: { x: 0, y: 0 },
      text: '',
    });

    const [pointTooltip, setPointTooltip] = useState({
      show: false,
      position: { x: 0, y: 0 },
      time: 0,
    });

    const {
      initialLoaded,
      seriesValues: values,
      loading,
    } = useSubscription(visualization.id, panelId);
    const { variables = [], configuration = {} } = visualization;
    const [{ height, width }, clientReference] = useClientSize();
    const legendReference = useRef(null);

    const addLegendHeight = configuration.legend?.show ? -legendHeight : 0;
    const addIsPreviewHeight = isPreview ? 0 : -53.5;
    const addErrorHeight = errors.length > 0 ? -32 : 0;

    const largestSize = Math.min(width, height);

    const vizSize =
      largestSize && largestSize + addLegendHeight + addIsPreviewHeight + addErrorHeight - 20;

    const pentagonSize = vizSize / (1 / 2) / Math.sqrt(3);
    const pentagonWidth = pentagonSize;
    const pentagonHeight = pentagonSize;

    useEffect(() => {
      if (legendReference.current) {
        setLegendHeight(legendReference.current.clientHeight);
      }
    }, [configuration.legend?.show]);

    useRefetchOnRangeChange({
      visualization,
      panelId,
      initialLoaded,
      selected,
      rangeKey: `${pollingDateRange.startDate}:${pollingDateRange.endDate}`,
    });

    const data = useDuvalPentagonData(values, variables, variablesIndex, isPreview);
    const series = useDuvalPentagonSeries(variables, variablesIndex, configuration);
    const rollingAverageData = usePentagonRolling(
      configuration,
      series,
      data,
      pentagonWidth,
      pentagonHeight
    );
    const hasNumericData = data?.some((entry) =>
      Object.keys(entry).some(
        (key) =>
          key !== 'time' &&
          key !== 'granularity' &&
          typeof entry[key] === 'number' &&
          !Number.isNaN(entry[key])
      )
    );
    const isVizEmpty = !hasNumericData && !loading && initialLoaded;
    const showOptionsMenu = !isPreview && !isVizEmpty;

    useEffect(() => {
      setIsAreaHighlighted({});
    }, [data]);

    const PointPlot = ({ plot, duvalMode }) => {
      const h2Serie = series.find((serie) => serie.id === plot.h2Id);
      const c2h6Serie = series.find((serie) => serie.id === plot.c2h6Id);
      const ch4Serie = series.find((serie) => serie.id === plot.ch4Id);
      const c2h4Serie = series.find((serie) => serie.id === plot.c2h4Id);
      const c2h2Serie = series.find((serie) => serie.id === plot.c2h2Id);
      const allSeriesPresent = h2Serie && c2h6Serie && ch4Serie && c2h4Serie && c2h2Serie;

      // Calculate area data before early returns so we can use it in useEffect
      const filteredData = allSeriesPresent
        ? data.filter(
            (d) =>
              d[h2Serie.id] &&
              d[c2h6Serie.id] &&
              d[ch4Serie.id] &&
              d[c2h4Serie.id] &&
              d[c2h2Serie.id]
          )
        : [];

      const h2 = h2Serie && filteredData.at(-1)?.[h2Serie.id];
      const c2h6 = c2h6Serie && filteredData.at(-1)?.[c2h6Serie.id];
      const ch4 = ch4Serie && filteredData.at(-1)?.[ch4Serie.id];
      const c2h4 = c2h4Serie && filteredData.at(-1)?.[c2h4Serie.id];
      const c2h2 = c2h2Serie && filteredData.at(-1)?.[c2h2Serie.id];

      const lastPointArea =
        filteredData.length > 0
          ? getCurrentPentagonArea({
              h2,
              c2h6,
              ch4,
              c2h4,
              c2h2,
              duvalType: plot.duvalType,
              pentagonWidth,
              pentagonHeight,
            })
          : null;

      // Call useEffect before any early returns
      useEffect(() => {
        if (lastPointArea) {
          setIsAreaHighlighted((state) => {
            if (state[lastPointArea]) return state;
            return { ...state, [lastPointArea]: true };
          });
        }
      }, [lastPointArea]);

      if (!allSeriesPresent) {
        return null;
      }

      if (filteredData.length === 0) {
        return null;
      }

      switch (duvalMode) {
        case 'current': {
          return (
            <PentagonPoint
              color={resolveColor(plot.color)}
              length={0}
              last={true}
              h2={h2}
              ch4={ch4}
              c2h6={c2h6}
              c2h4={c2h4}
              c2h2={c2h2}
              time={filteredData.at(-1).time}
            />
          );
        }
        case 'over time': {
          return (
            <OverTimePointPlot
              h2Serie={h2Serie}
              c2h6Serie={c2h6Serie}
              ch4Serie={ch4Serie}
              c2h4Serie={c2h4Serie}
              c2h2Serie={c2h2Serie}
              color={resolveColor(plot.color)}
            />
          );
        }
        default: {
          return null;
        }
      }
    };

    function PentagonPoint({
      idx: index = 0,
      h2 = 'Missing',
      c2h6 = 'Missing',
      ch4 = 'Missing',
      c2h4 = 'Missing',
      c2h2 = 'Missing',
      color = 0,
      last = false,
      length = 1,
      time = 0,
    }) {
      if (h2 === 0 && c2h6 === 0 && ch4 === 0 && c2h4 === 0 && c2h2 === 0 && errors.length === 0) {
        setErrors((state) => [
          ...state,
          `All recieved values are 0. - ${tooltipLabelFormatter(time, timezone)}`,
        ]);
      }
      if ((h2 < 0 || c2h6 < 0 || ch4 < 0 || c2h4 < 0 || c2h2 < 0) && errors.length === 0) {
        setErrors((state) => [
          ...state,
          `Negative value recieved. - ${tooltipLabelFormatter(time, timezone)}`,
        ]);
      }

      const gasses = [h2, c2h6, ch4, c2h4, c2h2];

      const centroidCoordinates = getGasCentroid(gasses);

      return (
        <>
          <circle
            r={last ? configuration.pointRadius + 2 : configuration.pointRadius}
            cx={`${50 + normalizeCoordinate(centroidCoordinates.x)}%`}
            cy={`${50 + normalizeCoordinate(centroidCoordinates.y)}%`}
            className="duval-point"
            strokeWidth={2}
            stroke={
              (time === pointTooltip.time && pointTooltip.show && 'black') ||
              (last && 'white') ||
              ''
            }
            style={{ opacity: `${(100 / length) * (index + 1)}%` }}
            fill={color}
            onMouseOut={() => {
              setPointTooltip((prev) => ({ ...prev, show: false }));
            }}
            onMouseOver={(event) => {
              if (pointTooltip.show || areaTooltip.show) {
                return;
              }
              setPointTooltip((prev) => ({
                ...prev,
                show: true,
                time,
                position: { x: event.clientX + 10, y: event.clientY + 10 },
              }));
            }}
          />
        </>
      );
    }

    const OverTimePointPlot = ({
      h2Serie = null,
      c2h6Serie = null,
      ch4Serie = null,
      c2h4Serie = null,
      c2h2Serie = null,
      color,
    }) => {
      // Filtering out missing datapoints which cannot be plotted on the duval
      const filteredData = data.filter(
        (d) =>
          d[h2Serie.id] && d[c2h6Serie.id] && d[ch4Serie.id] && d[c2h4Serie.id] && d[c2h2Serie.id]
      );
      return (
        h2Serie &&
        c2h6Serie &&
        ch4Serie &&
        c2h4Serie &&
        c2h2Serie &&
        filteredData.map((point, index) => {
          const h2 = point[h2Serie.id];
          const c2h6 = point[c2h6Serie.id];
          const ch4 = point[ch4Serie.id];
          const c2h4 = point[c2h4Serie.id];
          const c2h2 = point[c2h2Serie.id];

          return (
            <PentagonPoint
              color={color}
              length={filteredData.length}
              idx={index}
              key={index + h2Serie.id + c2h6Serie.id + ch4Serie.id}
              last={index === filteredData.length - 1}
              h2={h2}
              ch4={ch4}
              c2h6={c2h6}
              c2h4={c2h4}
              c2h2={c2h2}
              time={point.time}
            />
          );
        })
      );
    };

    const PentagonAreas = () =>
      getPentagonAreas(configuration.duvalType).map((area) => (
        <DrawPentagonArea
          key={area.name}
          area={area}
          transparent={false}
          pentagonWidth={pentagonWidth}
          pentagonHeight={pentagonHeight}
          areaTooltip={areaTooltip}
          setAreaTooltip={setAreaTooltip}
          pointTooltip={pointTooltip}
        />
      ));

    const Errors = () => (
      <div style={{ textAlign: 'center', maxWidth: width }}>Signal Error: {errors[0]}</div>
    );

    const handleDownloadClick = () => {
      onExportData(data[0].granularity);
    };
    const handleSelect = () => {
      onSelect(visualization);
    };
    const classes = classnames('menu', {});

    const vizHeight = height - 28; // less header
    const loaderHeight = vizHeight - 28; // less loader padding

    if (series.length < signalCounts?.minSignal) {
      return (
        <div className="star-error-container">
          <Icon
            icon="he-info"
            size="s"
          />
          <span className="error-message">
            <span className="error-bold">{visualizationName}</span> visualizations require between
            <span className="error-bold"> {signalCounts?.minSignal} </span> and
            <span className="error-bold"> {signalCounts?.maxSignal} signals</span>
          </span>
        </div>
      );
    }

    return (
      <div
        className={`duval-visualization-component ${selected ? 'checked' : ''}`}
        ref={clientReference}
      >
        {!isPreview && !isFullscreen ? (
          <div
            className="duval-header"
            onDoubleClick={(event) => {
              event.stopPropagation();
            }}
          >
            <div className="header-title truncateText">
              {!hideToolbar && (
                <div
                  className={`select ${selected ? 'checked' : ''}`}
                  onClick={handleSelect}
                >
                  <Icon
                    icon={`${selected ? 'he-checkbox-selected' : 'he-checkbox'}`}
                    size="s"
                  />
                </div>
              )}
              <CustomTooltip content={configuration?.title || visualization?.name}>
                <span className="title truncateText">
                  {configuration?.title || visualization?.name || '\u00A0'}
                </span>
              </CustomTooltip>
            </div>

            <div className={classes}>
              {showOptionsMenu && (
                <DropdownMenu
                  trigger="he-moreoptionsvertical"
                  triggerSize="s"
                  menuOptions={getMoreOptions({
                    site,
                    onFullscreen,
                    handleDownloadClick,
                    openSignalViewer,
                  })}
                  menuXPlacement={dropdownPlacement?.x}
                  menuYPlacement={dropdownPlacement?.y}
                  styles={{
                    dropdownIndicator: { padding: '0px' },
                    option: { cursor: 'pointer' },
                  }}
                />
              )}
            </div>

            {initialLoaded && loading && (
              <Spinner
                size="s"
                className="spinner"
              />
            )}
          </div>
        ) : (
          !isFullscreen && (
            <div className="duval-preview-title">
              <span className="title">{configuration.title || visualization.name || '\u00A0'}</span>
            </div>
          )
        )}
        {!initialLoaded && loading && data.length === 0 ? (
          <Loader
            text="Loading ..."
            height={loaderHeight}
          />
        ) : (
          <div className="duval-wrapper">
            {areaTooltip.show && (
              <AreaToolTip
                position={areaTooltip.position}
                text={areaTooltip.text}
              />
            )}

            {pointTooltip.show && (
              <PointToolTip
                pointTooltip={pointTooltip}
                setPointTooltip={setPointTooltip}
                timezone={timezone}
                data={data}
                series={series}
                plots={configuration.plots}
                pentagonWidth={pentagonWidth}
                pentagonHeight={pentagonHeight}
              />
            )}

            <div width="100%">
              <div
                className="duval-container"
                onMouseOut={() => {
                  setAreaTooltip((prev) => ({ ...prev, show: false }));
                  setPointTooltip((prev) => ({ ...prev, show: false }));
                }}
              >
                <svg
                  id={`duval${visualization.id}`}
                  className="duval-surface"
                  width={pentagonWidth}
                  height={pentagonHeight}
                >
                  <PentagonAreas />

                  <PentagonAreaLines duvalType={configuration.duvalType} />

                  {configuration.highlightAreas?.show && (
                    <PentagonAreaHighlights
                      duvalType={configuration.duvalType}
                      isAreaHighlighted={isAreaHighlighted}
                      pentagonWidth={pentagonWidth}
                      pentagonHeight={pentagonHeight}
                    />
                  )}

                  <PentagonOutline />

                  <PentagonSummitLabels />

                  {configuration.plots &&
                    configuration.plots.map((plot, index) => (
                      <PointPlot
                        key={index}
                        plot={plot}
                        duvalMode={configuration.duvalMode ?? ''}
                      />
                    ))}

                  {configuration.rollingAverage?.show && (
                    <g>
                      {rollingAverageData?.map((rollingPlot, index) => (
                        <RollingAveragePlot
                          key={index}
                          rollingPlot={rollingPlot}
                          configuration={configuration}
                        />
                      ))}
                    </g>
                  )}
                </svg>
                {errors.length > 0 ? <Errors /> : null}
                {configuration.legend && configuration.legend.show && (
                  <PentagonLegend
                    plots={configuration.plots}
                    rollingAverage={configuration.rollingAverage}
                    legendRef={legendReference}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  },
  (prevProperties, props) => JSON.stringify(prevProperties) === JSON.stringify(props)
);

export default DuvalPentagonVisualization;
